import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Subscription } from 'rxjs';
import { ConfirmService, ConfirmDialogState } from '../../../../core/services/confirm.service';
import { ButtonComponent } from '../button/button.component';

@Component({
  selector: 'app-global-confirm-dialog',
  standalone: true,
  imports: [ButtonComponent],
  template: `
    @if (currentState?.show) {
      <div class="fixed inset-0 z-50 flex items-center justify-center">
        <!-- Backdrop -->
        <div class="absolute inset-0 bg-black/75 backdrop-blur-sm" (click)="onCancel()"></div>
        
        <!-- Modal -->
        <div class="relative bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
          <!-- Header -->
          <div class="px-6 py-4 border-b border-zinc-800">
            <div class="flex items-center gap-3">
              <div [class]="iconClasses">
                <i [class]="icon"></i>
              </div>
              <h3 class="text-lg font-semibold text-zinc-100">{{ currentTitle }}</h3>
            </div>
          </div>
          
          <!-- Body -->
          <div class="px-6 py-4">
            <p class="text-zinc-300">{{ currentMessage }}</p>
            @if (currentDetails) {
              <p class="text-sm text-zinc-500 mt-2">{{ currentDetails }}</p>
            }
          </div>
          
          <!-- Actions -->
          <div class="px-6 py-4 bg-zinc-800/50 border-t border-zinc-800 flex gap-3 justify-end">
            <app-button variant="secondary" (click)="onCancel()">
              {{ currentCancelText }}
            </app-button>
            <app-button [variant]="confirmVariant" (click)="onConfirm()">
              @if (confirmIcon) {
                <i [class]="confirmIcon + ' mr-1.5'"></i>
              }
              {{ currentConfirmText }}
            </app-button>
          </div>
        </div>
      </div>
    }
  `,
  styles: [`
    :host {
      display: block;
    }
  `]
})
export class GlobalConfirmDialogComponent implements OnInit, OnDestroy {
  currentState: ConfirmDialogState | null = null;
  private subscription: Subscription | null = null;

  private confirmService = inject(ConfirmService);

  ngOnInit(): void {
    this.subscription = this.confirmService.dialogState$.subscribe(state => {
      this.currentState = state;
    });
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  get iconClasses(): string {
    const base = 'w-8 h-8 rounded-full flex items-center justify-center text-sm';
    const type = this.currentState?.options.type || 'warning';
    const map: Record<string, string> = {
      delete: 'bg-red-500/20 text-red-400 border border-red-500/30',
      warning: 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30',
      info: 'bg-blue-500/20 text-blue-400 border border-blue-500/30',
      danger: 'bg-red-500/20 text-red-400 border border-red-500/30',
    };
    return `${base} ${map[type]}`;
  }

  get icon(): string {
    const type = this.currentState?.options.type || 'warning';
    const map: Record<string, string> = {
      delete: 'fa-solid fa-trash',
      warning: 'fa-solid fa-exclamation-triangle',
      info: 'fa-solid fa-info-circle',
      danger: 'fa-solid fa-exclamation-triangle',
    };
    return map[type];
  }

  get confirmVariant(): 'primary' | 'danger' {
    const type = this.currentState?.options.type || 'warning';
    return type === 'delete' || type === 'danger' ? 'danger' : 'primary';
  }

  get confirmIcon(): string {
    return this.currentState?.options.confirmIcon || '';
  }

  get currentTitle(): string {
    return this.currentState?.options.title || 'Confirm Action';
  }

  get currentMessage(): string {
    return this.currentState?.options.message || 'Are you sure you want to proceed?';
  }

  get currentDetails(): string | undefined {
    return this.currentState?.options.details;
  }

  get currentCancelText(): string {
    return this.currentState?.options.cancelText || 'Cancel';
  }

  get currentConfirmText(): string {
    return this.currentState?.options.confirmText || 'Confirm';
  }

  onConfirm(): void {
    if (this.currentState) {
      this.confirmService.close(true, this.currentState.resolve);
    }
  }

  onCancel(): void {
    if (this.currentState) {
      this.confirmService.close(false, this.currentState.resolve);
    }
  }
}
